import { gql, useQuery } from '@apollo/client';

import { Topics } from '@app/context/constants';
import { PROCESSING_CONSENT_FIELDS, ProcessingConsent } from '@dieterApi/questionnaire/useAddProcessingConsentMutation';
import { SCAN_RESULT_FIELDS, ScanResultData } from '@dieterApi/scan/useScanResultQuery';
import { ISignatureRequest, SIGNATURE_REQUEST_FIELDS } from '@dieterApi/signature/useSignatureRequestCreateMutation';
import { Coupon, Employee, EMPLOYEE_FIELDS, ProductFeature, UserRole } from '@dieterApi/user/useUserQuery';
import { WHITELABEL_CONFIG_FIELDS, WhitelabelConfig } from '@dieterApi/whitelabel/useWhitelabelConfigUpsertMutation';

export const BASIC_COMPANY_FIELDS = gql`
  fragment BasicCompanyFields on Company {
    id
    name
    city
    street
    zip
    phone
    website
    subscription
    isPrincipal
    isAgency
    isDpo
    isPlus
    couponCode
    celloReferralCode
    stripeCustomerId
    usercentricsSettingsId
    wixAppInstanceId
    shopifyCallbackUrl
    topicsActivated
    numberOfTickets
    numberOfScans
    features
    maxEmployees
    assignedDpoUserId
  }
`;

export const GET_COMPANIES = gql`
  query GetCompanies($onlyActive: Boolean) {
    getCompanies(onlyActive: $onlyActive) {
      id
      name
      subscription
    }
  }
`;

export const GET_COMPANY_BASIC = gql`
  ${BASIC_COMPANY_FIELDS}

  query GetCompanyBasic($companyId: String!) {
    getCompany(companyId: $companyId) {
      ...BasicCompanyFields
    }
  }
`;

export const COMPANY_FIELDS = gql`
  ${BASIC_COMPANY_FIELDS}
  ${EMPLOYEE_FIELDS}
  ${SCAN_RESULT_FIELDS}
  ${PROCESSING_CONSENT_FIELDS}
  ${WHITELABEL_CONFIG_FIELDS}
  ${SIGNATURE_REQUEST_FIELDS}

  fragment CompanyFields on Company {
    ...BasicCompanyFields
    coupons {
      code
      createdAt
    }
    processingConsents {
      ...ProcessingConsentFields
    }
    users {
      id
      createdAt
      email
      firstName
      lastName
      confirmed
      role
      lastLogin
      mfaEnabled
    }
    employees {
      ...UserEmployeeFields
    }
    scanResult {
      ...ScanResultFields
    }
    scanResults {
      ...ScanResultFields
    }
    whitelabelConfig {
      ...WhitelabelConfigFields
    }
    signatureRequests {
      ...SignatureRequestFields
    }
  }
`;

export const GET_COMPANY = gql`
  ${COMPANY_FIELDS}

  query GetCompany($companyId: String!) {
    getCompany(companyId: $companyId) {
      ...CompanyFields
    }
  }
`;

export enum CompanySubscription {
  Ticket = 'Ticket',
  Basic = 'Basic',
  Premium = 'Premium',
}

export interface GetCompaniesInput {
  onlyActive?: boolean;
}

export interface GetCompaniesResult {
  getCompanies: {
    id: string;
    name: string;
    subscription: CompanySubscription;
  }[];
}

export interface BasicCompany {
  id: string;
  name: string;
  city: string;
  street: string;
  zip: string;
  phone: string;
  website: string;
  subscription: CompanySubscription;
  isPrincipal: boolean;
  isAgency: boolean;
  isDpo: boolean;
  isPlus: boolean;
  features: ProductFeature[];
  couponCode?: string;
  celloReferralCode?: string;
  stripeCustomerId?: string;
  usercentricsSettingsId?: string;
  wixAppInstanceId?: string;
  shopifyCallbackUrl?: string;
  topicsActivated: Topics[];
  maxEmployees: number;
  numberOfTickets?: number;
  numberOfScans: number;
  assignedDpoUserId?: string;
}

export interface Company extends BasicCompany {
  coupons: Coupon[];
  processingConsents: ProcessingConsent[];
  users: {
    id: string;
    createdAt: string;
    email: string;
    firstName?: string;
    lastName?: string;
    confirmed: boolean;
    role: UserRole;
    mfaEnabled: boolean;
    lastLogin: string;
  }[];
  employees: Employee[];
  stakeholders: Employee[];
  scanResult: ScanResultData;
  scanResults: ScanResultData[];
  whitelabelConfig: WhitelabelConfig;
  signatureRequests?: ISignatureRequest[];
}

export interface GetCompanyResult {
  getCompany: Company;
}

export interface GetCompanyBasicResult {
  getCompany: BasicCompany;
}

export interface GetCompanyInput {
  companyId?: string;
}

export function useCompaniesQuery({ variables }: { variables: GetCompaniesInput }) {
  return useQuery<GetCompaniesResult, GetCompaniesInput>(GET_COMPANIES, { variables });
}

export function useCompanyQuery(companyId: string) {
  return useQuery<GetCompanyResult, GetCompanyInput>(GET_COMPANY, {
    variables: { companyId },
    skip: !companyId,
  });
}

export function useCompanyBasicQuery(companyId: string) {
  return useQuery<GetCompanyBasicResult, GetCompanyInput>(GET_COMPANY_BASIC, {
    variables: { companyId },
    skip: !companyId,
  });
}
